import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend, BarChart, Bar, XAxis, YAxis, CartesianGrid, LineChart, Line,
} from 'recharts';
import { api } from '@/api/client';
import { SearchableSelect } from '@/components/ui/SearchableSelect';
import { dateTime, money, useLeadUsers } from './leads.api';
import { LeadsSubNav } from './LeadsSubNav';

/** Colours reused across the three charts so a status keeps its colour from one to the next. */
const COLORS = ['#0ea5e9', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444', '#14b8a6', '#ec4899', '#6366f1', '#84cc16', '#f97316'];

interface Dash {
  range: { from: string; to: string };
  cards: {
    total: number; newInRange: number; open: number; won: number; lost: number; converted: number; deleted: number;
    conversionRate: number; winRate: number; pipelineValue: number; monthlyOrders: number; dueToday: number; overdue: number;
  };
  byStatus: { id: number | null; name: string; colour: string | null; count: number; value: number }[];
  bySource: { id: number | null; name: string; count: number }[];
  byOwner: { id: number | null; name: string; count: number; value: number }[];
  byRating: { name: string; count: number }[];
  trend: { key: string; label: string; count: number }[];
  recent: { id: number; type: string; message: string; at: string; userName: string | null; leadId: number; leadNo?: string; company?: string }[];
}

function Card({ label, value, hint, tone, to }: { label: string; value: any; hint?: string; tone?: string; to?: string }) {
  const body = (
    <div className={`bg-white dark:bg-slate-900 border rounded p-3 ${to ? 'hover:border-emerald-400 transition' : ''}`}>
      <div className="text-[11px] uppercase tracking-wide text-gray-500">{label}</div>
      <div className={`text-2xl font-semibold mt-0.5 ${tone ?? 'text-slate-800 dark:text-slate-100'}`}>{value}</div>
      {hint && <div className="text-[11px] text-gray-400 mt-0.5">{hint}</div>}
    </div>
  );
  return to ? <Link to={to}>{body}</Link> : body;
}

export function LeadsDashboardPage() {
  const { data: users } = useLeadUsers();
  const [ownerId, setOwnerId] = useState<string | number | ''>('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const params = useMemo(() => {
    const p: any = {};
    if (ownerId) p.ownerId = ownerId;
    if (fromDate) p.fromDate = fromDate;
    if (toDate) p.toDate = toDate;
    return p;
  }, [ownerId, fromDate, toDate]);

  const { data, isLoading } = useQuery<Dash>({
    queryKey: ['lead-dashboard', params],
    queryFn: async () => (await api.get<Dash>('/leads/dashboard', { params })).data,
  });

  const c = data?.cards;

  return (
    <div className="p-6">
      <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold">Leads Dashboard</h1>
          <p className="text-xs text-gray-500 mt-0.5">Where the pipeline stands, and what needs calling today.</p>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <label className="text-xs text-gray-500 block">Sale Person</label>
            <SearchableSelect value={ownerId} onChange={setOwnerId} allowClear placeholder="Everyone"
              options={(users ?? []).map((u) => ({ value: u.id, label: u.name }))} className="min-w-[180px]" />
          </div>
          <div>
            <label className="text-xs text-gray-500 block">From</label>
            <input type="date" className="border rounded px-2 py-1.5 text-sm dark:bg-slate-900" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
          </div>
          <div>
            <label className="text-xs text-gray-500 block">To</label>
            <input type="date" className="border rounded px-2 py-1.5 text-sm dark:bg-slate-900" value={toDate} onChange={(e) => setToDate(e.target.value)} />
          </div>
        </div>
      </div>

      <LeadsSubNav />

      {isLoading ? (
        <div className="text-center py-16 text-gray-400">Loading…</div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3 mb-4">
            <Card label="Total leads" value={(c?.total ?? 0).toLocaleString('en-IN')} to="/leads/manage" />
            <Card label="New in range" value={(c?.newInRange ?? 0).toLocaleString('en-IN')} />
            <Card label="Open" value={(c?.open ?? 0).toLocaleString('en-IN')} hint="not won or lost" />
            <Card label="Won" value={(c?.won ?? 0).toLocaleString('en-IN')} tone="text-emerald-600" />
            <Card label="Lost" value={(c?.lost ?? 0).toLocaleString('en-IN')} tone="text-rose-600" />
            <Card label="Signed up" value={(c?.converted ?? 0).toLocaleString('en-IN')} hint={`${c?.conversionRate ?? 0}% of all leads`} tone="text-emerald-700" />
            <Card label="Pipeline value" value={money(c?.pipelineValue)} hint={`${(c?.monthlyOrders ?? 0).toLocaleString('en-IN')} orders/month`} />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <Card label="Due today" value={(c?.dueToday ?? 0).toLocaleString('en-IN')} hint="follow-ups planned for today" to="/leads/followups" tone="text-amber-600" />
            <Card label="Overdue" value={(c?.overdue ?? 0).toLocaleString('en-IN')} hint="should already have been called" to="/leads/followups" tone="text-rose-600" />
            <Card label="Win rate" value={`${c?.winRate ?? 0}%`} hint="won ÷ (won + lost)" />
            <Card label="In Trash" value={(c?.deleted ?? 0).toLocaleString('en-IN')} to="/leads/trash" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
            <div className="bg-white dark:bg-slate-900 border rounded p-3">
              <h3 className="text-sm font-semibold mb-2">By status</h3>
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie data={data?.byStatus ?? []} dataKey="count" nameKey="name" outerRadius={85} label={(e: any) => `${e.name}: ${e.count}`}>
                    {(data?.byStatus ?? []).map((s, i) => <Cell key={i} fill={s.colour ?? COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="bg-white dark:bg-slate-900 border rounded p-3">
              <h3 className="text-sm font-semibold mb-2">By source</h3>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={data?.bySource ?? []}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-20} textAnchor="end" height={50} />
                  <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#0ea5e9" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="bg-white dark:bg-slate-900 border rounded p-3">
              <h3 className="text-sm font-semibold mb-2">New leads per month</h3>
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={data?.trend ?? []}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="count" name="Leads" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-white dark:bg-slate-900 border rounded overflow-hidden">
              <h3 className="text-sm font-semibold px-3 py-2 border-b bg-slate-50 dark:bg-slate-800">By sale person</h3>
              <table className="w-full text-sm">
                <thead className="text-xs text-gray-500">
                  <tr><th className="text-left px-3 py-1.5">Sale Person</th><th className="text-right px-3 py-1.5">Leads</th><th className="text-right px-3 py-1.5">Pipeline value</th></tr>
                </thead>
                <tbody>
                  {(data?.byOwner ?? []).map((o, i) => (
                    <tr key={i} className="border-t">
                      <td className={`px-3 py-1.5 ${o.id ? '' : 'text-gray-400 italic'}`}>{o.name}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{o.count}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{money(o.value)}</td>
                    </tr>
                  ))}
                  {!(data?.byOwner ?? []).length && <tr><td colSpan={3} className="px-3 py-4 text-center text-gray-400 text-xs">No leads yet.</td></tr>}
                </tbody>
              </table>
            </div>

            <div className="bg-white dark:bg-slate-900 border rounded overflow-hidden">
              <h3 className="text-sm font-semibold px-3 py-2 border-b bg-slate-50 dark:bg-slate-800">Latest activity</h3>
              <ol className="divide-y max-h-[320px] overflow-y-auto">
                {(data?.recent ?? []).map((a) => (
                  <li key={a.id} className="px-3 py-2 text-xs">
                    <span className="font-medium text-slate-700 dark:text-slate-200">{a.company ?? a.leadNo}</span>
                    <span className="text-slate-600 dark:text-slate-300"> — {a.message}</span>
                    <div className="text-[11px] text-gray-400">{dateTime(a.at)}{a.userName ? ` · ${a.userName}` : ''}</div>
                  </li>
                ))}
                {!(data?.recent ?? []).length && <li className="px-3 py-4 text-center text-gray-400 text-xs">Nothing has happened yet.</li>}
              </ol>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
